import {
  ProductionListType,
  ProductionVMType,
  UpdateProductionType,
  UpdateProductionTaskType,
  ProductionTaskListType,
  ProductionsSummaryType,
  CreateProductionTaskType,
  CreateProductionType,
  ProductionTaskType,
  DelayReasonOptionType,
  ChangeProductionDeadlineType,
  ProductionDeadlineChangeType,
} from "types/ProductionType";
import { FetchError } from "util/exceptions";
import { API_URL } from "./common";

export const apiGetProduction = (
  token: string,
  conf_id: number
): Promise<ProductionVMType> => {
  return fetch(`${API_URL}/v1/ecommerce/productions/${conf_id}`, {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  }).then((res) => {
    if (res.ok) return res.json();
    else throw new FetchError(res.statusText, res.status);
  });
};

export const apiGetProductionsSummary = (
  token: string
): Promise<ProductionsSummaryType> => {
  return fetch(`${API_URL}/v1/ecommerce/productions/summary`, {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  }).then((res) => {
    if (res.ok) return res.json();
    else throw new FetchError(res.statusText, res.status);
  });
};

export const apiCreateProduction = (
  token: string,
  body: CreateProductionType
): Promise<ProductionVMType> => {
  const requestOptions = {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  };
  return fetch(`${API_URL}/v1/ecommerce/productions`, requestOptions).then(
    (res) => {
      if (res.ok) {
        return res.json();
      }
    }
  );
};

export const apiUpdateProduction = (
  token: string,
  id: number,
  body: UpdateProductionType
): Promise<ProductionVMType> => {
  const requestOptions = {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  };
  return fetch(
    `${API_URL}/v1/ecommerce/productions/${id}`,
    requestOptions
  ).then((res) => {
    if (res.ok) {
      return res.json();
    }
  });
};

export const apiCreateProductionTask = (
  token: string,
  body: CreateProductionTaskType
): Promise<ProductionVMType> => {
  const requestOptions = {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  };
  return fetch(
    `${API_URL}/v1/ecommerce/productions/tasks`,
    requestOptions
  ).then((res) => {
    if (res.ok) {
      return res.json();
    }
  });
};

export const apiUpdateProductionTask = (
  token: string,
  id: number,
  body: UpdateProductionTaskType
) => {
  const requestOptions = {
      method: "PUT",
      headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(body),
  };

  return fetch(`${API_URL}/v1/ecommerce/productions/tasks/${id}`, requestOptions)
      .then((res) => {
          if (!res.ok) {
              // The API returns make_response(message, code) - a plain text
              // body, not JSON. This used to call res.json(), so every failed
              // update threw the JSON parse error instead of the server's
              // message and the reason was lost. EE-216 needs the message
              // intact to recognise DELAY_REASON_REQUIRED.
              return res.text().then((message) => {
                  throw new FetchError(
                      message || res.statusText || "API error occurred.",
                      res.status
                  );
              });
          }
          return res.json();
      });
};

// EE-216: the delay reasons - one list for the QC delay, the order
// completion delay and the Production Deadline change. A setup table
// backend-side, so the labels are fetched, never listed here.
export const apiGetDelayReasons = (
  token: string
): Promise<DelayReasonOptionType[]> => {
  return fetch(`${API_URL}/v1/ecommerce/productions/delay-reasons`, {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  }).then((res) => {
    if (res.ok) return res.json().then((body) => body.reasons ?? []);
    else throw new FetchError(res.statusText, res.status);
  });
};

// Move the Production Deadline of one or more parts. One row or a
// ticked selection - a single production is a list of one - and the backend
// applies the whole list or none of it.
export const apiChangeProductionDeadline = (
  token: string,
  body: ChangeProductionDeadlineType
): Promise<ProductionVMType[]> => {
  return fetch(`${API_URL}/v1/ecommerce/productions/deadline`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  }).then((res) => {
    if (res.ok) return res.json().then((data) => data.productions ?? []);
    else throw new FetchError(res.statusText, res.status);
  });
};


// The recorded moves of one production's deadline, newest first. Fetched only
// when the history is opened - every row of the table already carries the
// latest move, and the trail is worth a request only when someone asks for it.
export const apiGetProductionDeadlineChanges = (
  token: string,
  id: number
): Promise<ProductionDeadlineChangeType[]> => {
  return fetch(`${API_URL}/v1/ecommerce/productions/${id}/deadline-changes`, {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  }).then((res) => {
    if (res.ok) return res.json().then((body) => body.changes ?? []);
    else throw new FetchError(res.statusText, res.status);
  });
};

export const apiDeleteProductionTask = (
  token: string,
  id: number
): Promise<void> => {
  return fetch(`${API_URL}/v1/ecommerce/productions/tasks/${id}`, {
    method: "DELETE",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  }).then((res) => {
    if (!res.ok) throw new FetchError(res.statusText, res.status);
  });
};

export const apiGetProductions = (
  token: string,
  page: number,
  pageSize: number,
  filter: string,
  sortKey: "order_id" | "deadline_date" | "priority" = "order_id",
  sortDirection: "asc" | "desc" = "desc",
  search?: string,
  nextTask?: string,
  // "cam"
  taskStates?: string,
  taskFilter?: string
  // "cam=ACTIVE"
): Promise<ProductionListType> => {
  const url = `${API_URL}/v1/ecommerce/productions` +
            `?page=${page}` +
            `&pageSize=${pageSize}` +
            `&sortKey=${sortKey}` +
            `&sortDirection=${sortDirection}` +
            `${search ? `&search=${search}` : ""}` +
            `${nextTask ? `&nextTask=${nextTask}` : ""}` +
            `${filter ? `&${filter}` : ""}` +
            `${taskStates ? `&taskStates=${taskStates}` : ""}` +
            `${taskFilter ? `&taskFilter=${taskFilter}` : ""}`;
  console.log(url);
  return fetch(url, {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  }).then((res) => {
    if (res.ok) return res.json();
    else throw new FetchError(res.statusText, res.status);
  });
};

export const apiGetProductionTasks = (
  token: string,
  page: number,
  pageSize: number,
  type: string,
  userId?: number,
  machineId?: number
): Promise<ProductionTaskListType> => {
  return fetch(
    `${API_URL}/v1/ecommerce/productions/tasks?page=${page}&pageSize=${pageSize}&name=${type}${
      userId ? "&userId=" + userId : ""
    }${machineId ? "&machineId=" + machineId : ""}`,
    {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    }
  ).then((res) => {
    if (res.ok) return res.json();
    else throw new FetchError(res.statusText, res.status);
  });
};

export const apiGetProductionsReport = (
  token: string
): Promise<void> => {
  return fetch(`${API_URL}/v1/ecommerce/productions/report`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((res) => {
      if (res.ok) return res.blob();
      else throw new FetchError(res.statusText, res.status);
    })
    .then((blob) => {
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement("a");
      link.href = url;
      const today = new Date().toISOString().split("T")[0];
      link.setAttribute("download", `Manufacturing_Report_${today}.xlsx`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    });
};

export const apiGetProductionDatasheet = (
  token: string,
  id: number,
  productId: number
): Promise<void> => {
  return fetch(`${API_URL}/v1/ecommerce/productions/${id}/datasheet`, {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  })
    .then((res) => {
      if (res.ok) return res.blob();
      else throw new FetchError(res.statusText, res.status);
    })
    .then((blob) => {
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `Setup Sheet #${productId}.xlsx`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    });
};

// The printable Production Cards for the shop-floor board, three per A4 page
// with cut lines. One row's menu sends a single id, the bulk print the ticked
// rows; the cards come out in the order given.
export const apiGetProductionCards = (
  token: string,
  productionIds: number[]
): Promise<void> => {
  return fetch(`${API_URL}/v1/ecommerce/productions/cards`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ productionIds }),
  })
    .then((res) => {
      if (res.ok) return res.blob();
      else throw new FetchError(res.statusText, res.status);
    })
    .then((blob) => {
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `Production_Cards_${new Date().toISOString().slice(0, 10)}.pdf`
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    });
};

export const apiGetProductTasks = (
  token: string,
  productId: number
): Promise<ProductionTaskType[]> => {
  // Use the apiGetProductions method to get the production by productId filter
  return apiGetProductions(
    token,
    1,
    1,
    `filter=productId=${productId}`, // Filter by productId
    "order_id",
    "desc"
  )
    .then((productionList) => {
      return productionList.items[0].tasks; // Return the tasks from the matching production
    })
    .catch((error) => {
      console.error("Failed to fetch production tasks:", error);
      return Promise.reject(error);
    });
};

export const apiDeleteProduction = (
  token: string,
  id: number
): Promise<void> => {
  return fetch(`${API_URL}/v1/ecommerce/productions/${id}`, {
    method: "DELETE",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  }).then((res) => {
    if (!res.ok) throw new FetchError(res.statusText, res.status);
  });
};

export const apiGetSimiliarModelProductions = (token: string, id: number): Promise<ProductionVMType[]> => {
  return fetch(`${API_URL}/v1/ecommerce/models/${id}/similiar`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  }).then((res) => {
    if (res.ok) return res.json();
    else throw new FetchError(res.statusText, res.status);
  });
};